import React, { useCallback, useRef, useEffect } from 'react';
import { Direction } from '../types';

interface TouchControlsOptions {
  onMove: (direction: Direction) => void;
  enabled?: boolean;
}

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
}

const MIN_SWIPE_DISTANCE = 30;
const MAX_SWIPE_TIME = 500;

export function useTouchControls({ onMove, enabled = true }: TouchControlsOptions) {
  const swipeState = useRef<SwipeState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use native event listeners to prevent browser back-swipe gesture
  // React's synthetic events are passive by default and can't preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (!enabled) return;

      const touch = e.touches[0];
      swipeState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now()
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!enabled || !swipeState.current) return;

      // Prevent default to stop browser back/forward navigation gestures
      // This is critical for iOS Safari and Chrome's swipe-to-navigate
      e.preventDefault();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!enabled || !swipeState.current) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - swipeState.current.startX;
      const dy = touch.clientY - swipeState.current.startY;
      const dt = Date.now() - swipeState.current.startTime;

      swipeState.current = null;

      // Must be a quick swipe
      if (dt > MAX_SWIPE_TIME) return;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Must meet minimum distance
      if (absDx < MIN_SWIPE_DISTANCE && absDy < MIN_SWIPE_DISTANCE) return;

      // Determine direction based on dominant axis
      if (absDx > absDy) {
        onMove(dx > 0 ? 'right' : 'left');
      } else {
        onMove(dy > 0 ? 'down' : 'up');
      }
    };

    // Use { passive: false } to allow preventDefault() on touch events
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, onMove]);

  // Legacy React handlers removed - now using native event listeners above
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Handled by native listener
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // Handled by native listener
  }, []);

  // Tap-based controls (tap screen regions)
  const handleTap = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    let clientX: number;
    let clientY: number;

    if ('touches' in e) {
      // Don't handle tap for touch - let swipe handle it
      return;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const width = rect.width;
    const height = rect.height;

    // Calculate relative position (0-1)
    const relX = x / width;
    const relY = y / height;

    // Determine direction based on tap region
    // Edges (25% from each side) trigger movement
    if (relY < 0.20) {
      onMove('up');
    } else if (relY > 0.80) {
      onMove('down');
    } else if (relX < 0.20) {
      onMove('left');
    } else if (relX > 0.80) {
      onMove('right');
    }
    // Center tap = no action
  }, [enabled, onMove]);

  return {
    containerRef,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onClick: handleTap
    }
  };
}
